import { useState } from 'react';
import { FiChevronDown, FiChevronUp, FiHelpCircle } from 'react-icons/fi';

/**
 * Componente de pregunta individual con acordeón
 */
function FAQItem({ question, answer, isOpen, onToggle }) {
  return (
    <div className="bg-white rounded-lg shadow-md mb-4 overflow-hidden transition-all">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-900 pr-4">{question}</span>
        {isOpen ? (
          <FiChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" />
        ) : (
          <FiChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
        )}
      </button>
      
      {isOpen && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="text-gray-700 space-y-2">
            {typeof answer === 'string' ? (
              <p>{answer}</p>
            ) : (
              answer
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Página de Preguntas Frecuentes (FAQ)
 */
export default function PreguntasFrecuentes() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: 'General',
      questions: [
        {
          question: '¿Qué es Shalom - Aplicaciones Hebreas?',
          answer: 'Shalom es una plataforma integral de estudio y conocimiento de las tradiciones hebreas. Incluye un calendario dual (Gregoriano/Hebreo Caraíta), lecturas bíblicas diarias, estudios bíblicos con concordancia Strong, análisis de los 613 mandamientos, información histórica verificada y artículos sobre teología y tradiciones hebreas.'
        },
        {
          question: '¿Necesito registrarme para usar la aplicación?',
          answer: (
            <>
              <p>No es necesario registrarse para acceder a la mayoría del contenido:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Calendario hebreo completo</li>
                <li>Lecturas de Torá y Profetas</li>
                <li>Lectura de la Biblia con múltiples versiones</li>
                <li>Artículos y estudios publicados</li>
                <li>Datos históricos verificados</li>
                <li>Listado de mandamientos</li>
              </ul>
              <p className="mt-2">El registro es opcional y te permite:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Crear notas personales en tus estudios bíblicos</li>
                <li>Añadir eventos personalizados al calendario</li>
                <li>Proponer nuevos datos históricos para validación</li>
                <li>Crear perspectivas personalizadas de los mandamientos</li>
              </ul>
            </>
          )
        },
        {
          question: '¿La aplicación es gratuita?',
          answer: 'Sí, Shalom es completamente gratuita. Todo el contenido está disponible sin costo alguno. Nuestro objetivo es facilitar el estudio y comprensión de las tradiciones hebreas para todos.'
        }
      ]
    },
    {
      category: 'Calendario Hebreo',
      questions: [
        {
          question: '¿Qué tipo de calendario hebreo utiliza la aplicación?',
          answer: 'Utilizamos el calendario Hebreo Caraíta, que se basa en la observación de la luna nueva (avistamiento del primer creciente lunar) para determinar el inicio de cada mes. Esto lo diferencia del calendario rabínico tradicional que usa cálculos astronómicos fijos.'
        },
        {
          question: '¿Cómo están organizadas las lecturas de Torá?',
          answer: 'Las lecturas siguen el ciclo de 54 Parashas Caraítas, que son las divisiones semanales tradicionales de la Torá. Cada día del calendario muestra la porción correspondiente de Torá y Profetas (Haftarah) según el ciclo anual de lectura.'
        },
        {
          question: '¿Puedo exportar el calendario?',
          answer: 'Sí, los usuarios registrados pueden exportar el calendario a formatos estándar (iCal, CSV) para sincronizarlo con otras aplicaciones como Google Calendar, Outlook, o Apple Calendar.'
        }
      ]
    },
    {
      category: 'Estudio Bíblico',
      questions: [
        {
          question: '¿Qué versiones de la Biblia están disponibles?',
          answer: 'Actualmente ofrecemos acceso a múltiples versiones de la Biblia en español, con planes de agregar versiones en hebreo, griego y arameo. Puedes visualizar múltiples versiones en paralelo para comparar traducciones.'
        },
        {
          question: '¿Qué es la Concordancia Strong y cómo funciona?',
          answer: 'La Concordancia Strong es un sistema de numeración que permite estudiar las palabras originales de la Biblia (hebreo en el Antiguo Testamento y griego en el Nuevo Testamento). Al hacer clic en un número Strong en el texto bíblico, se abre un panel inferior que muestra: la palabra original, su transliteración, definición, uso y contexto.'
        },
        {
          question: '¿Puedo guardar mis notas de estudio?',
          answer: 'Sí, los usuarios registrados pueden crear y guardar notas personales en cualquier versículo. Las notas se sincronizan automáticamente y están disponibles en todos tus dispositivos.'
        }
      ]
    },
    {
      category: 'Mandamientos (Mitzvot)',
      questions: [
        {
          question: '¿Qué son los 613 mandamientos?',
          answer: 'Los 613 mandamientos (Taryag Mitzvot) son el conjunto de preceptos y prohibiciones que, según la tradición rabínica, están contenidos en la Torá. Se dividen en 248 mandamientos positivos (cosas que debemos hacer) y 365 mandamientos negativos (cosas que no debemos hacer).'
        },
        {
          question: '¿Qué perspectivas de los mandamientos están disponibles?',
          answer: (
            <>
              <p>Ofrecemos múltiples perspectivas rabínicas y académicas:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li><strong>Rambam (Maimónides):</strong> Sefer HaMitzvot - Listado clásico</li>
                <li><strong>Ramban (Nahmánides):</strong> Hasagot - Comentarios y diferencias</li>
                <li><strong>SEMAG:</strong> Sefer Mitzvot Gadol - Organización por categorías</li>
                <li><strong>Sefer HaChinuch:</strong> Enfoque educativo</li>
                <li><strong>Ibn Ezra:</strong> Perspectiva filosófica</li>
                <li><strong>Perspectiva Personal:</strong> Crea tu propia lista (usuarios registrados)</li>
              </ul>
            </>
          )
        },
        {
          question: '¿Todos los mandamientos son aplicables hoy en día?',
          answer: 'No todos los mandamientos son aplicables en la actualidad. Muchos están relacionados con el Templo de Jerusalén (que fue destruido) o con sistemas de gobierno que ya no existen. La aplicación filtra y categoriza los mandamientos según su aplicabilidad actual.'
        }
      ]
    },
    {
      category: 'Historicidad',
      questions: [
        {
          question: '¿Cómo se verifica la historicidad de los datos?',
          answer: (
            <>
              <p>Cada dato histórico pasa por un riguroso proceso de validación en 4 etapas:</p>
              <ol className="list-decimal list-inside mt-2 space-y-2">
                <li><strong>Propuesta Inicial:</strong> Usuarios registrados proponen datos con fuentes</li>
                <li><strong>Revisión por Editores:</strong> Verificación y completado de información</li>
                <li><strong>Validación por Expertos:</strong> Historiadores asignan porcentajes de autenticidad</li>
                <li><strong>Aprobación Final:</strong> Gestores publican el dato verificado</li>
              </ol>
              <p className="mt-2">Solo los datos aprobados son visibles públicamente.</p>
            </>
          )
        },
        {
          question: '¿Qué significan los porcentajes de historicidad?',
          answer: 'Cada dato histórico incluye tres métricas: (1) Historicidad: evidencia histórica general, (2) Autenticidad: confirmación de fuentes primarias, (3) Veracidad: precisión de los detalles. Estos porcentajes son asignados por expertos basándose en evidencia arqueológica, documentación histórica y consenso académico.'
        },
        {
          question: '¿Puedo contribuir con información histórica?',
          answer: 'Sí, los usuarios registrados pueden proponer nuevos datos históricos. Debes incluir el tema, descripción detallada, fuentes de información y referencias. Tu propuesta pasará por el proceso de validación antes de ser publicada.'
        }
      ]
    },
    {
      category: 'Soporte Técnico',
      questions: [
        {
          question: '¿En qué dispositivos puedo usar Shalom?',
          answer: 'Actualmente Shalom está disponible como aplicación web, accesible desde cualquier navegador moderno en computadoras, tablets y teléfonos móviles. Las versiones nativas para iOS y Android están en desarrollo.'
        },
        {
          question: '¿Los datos se guardan localmente?',
          answer: 'Los usuarios no registrados pueden consultar todo el contenido, pero sus datos no se guardan. Los usuarios registrados tienen sus datos sincronizados en la nube y también se guardan localmente en el dispositivo para acceso offline.'
        },
        {
          question: '¿Cómo puedo reportar un error o sugerir una mejora?',
          answer: 'Puedes contactarnos a través del formulario de contacto en la sección "Contacto". Describe el error o tu sugerencia detalladamente y nos pondremos en contacto contigo lo antes posible.'
        }
      ]
    }
  ];

  const handleToggle = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <FiHelpCircle className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Preguntas Frecuentes
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Encuentra respuestas a las preguntas más comunes sobre Shalom y sus funcionalidades.
          </p>
        </div>

        {/* FAQs por categoría */}
        {faqs.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="w-2 h-8 bg-blue-600 rounded mr-3"></span>
              {category.category}
            </h2>
            
            <div>
              {category.questions.map((faq, questionIndex) => (
                <FAQItem
                  key={questionIndex}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === `${categoryIndex}-${questionIndex}`}
                  onToggle={() => handleToggle(categoryIndex, questionIndex)}
                />
              ))}
            </div>
          </div>
        ))}

        {/* CTA final */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-3">
            ¿No encontraste lo que buscabas?
          </h3>
          <p className="text-blue-100 mb-6">
            Estamos aquí para ayudarte. Contáctanos y te responderemos lo antes posible.
          </p>
          <a
            href="/contacto"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors shadow-lg"
          >
            Ir a Contacto
          </a>
        </div>
      </div>
    </div>
  );
}






